import { Link } from "react-router-dom";
import {
  Page,
  Alert,
  Card,
  KPI,
  KPIGrid,
  LoadingState,
  ErrorState,
  SectionHeader,
} from "@marekl/adminer/components";
import { formatNumber } from "@marekl/adminer/format";
import { RankedBars } from "../../components/RankedBars";
import { RefreshButton } from "../../components/RefreshButton";
import { useDashboardStats } from "../../lib/queries/dashboard";
import { runtimeConfig } from "../../lib/config";
import styles from "./DashboardPage.module.css";

/** Everything this page shows, for the header's refresh button. */
const REFRESH_KEYS = [["stats"]];

export function DashboardPage() {
  const stats = useDashboardStats();

  return (
    <Page>
      <Page.Header
        title="Overview"
        subtitle="delivery status at a glance"
        subtitleVariant="tagline"
        actions={<RefreshButton queryKeys={REFRESH_KEYS} live />}
      />
      <Page.Content stack gap="lg">
        {stats.isLoading ? (
          <LoadingState label="Loading statistics…" />
        ) : stats.error ? (
          <ErrorState
            error={stats.error}
            title="Failed to load statistics."
            onRetry={stats.refetch}
          />
        ) : stats.data ? (
          <>
            {stats.data.workers_online === 0 && stats.data.queued > 0 ? (
              <Alert variant="err" title="No worker is connected">
                {formatNumber(stats.data.queued)}{" "}
                {stats.data.queued === 1 ? "message is" : "messages are"} waiting
                to be sent. <Link to="/workers">Check workers →</Link>
              </Alert>
            ) : stats.data.oldest_due_seconds > 300 ? (
              <Alert
                variant="warn"
                title={`The oldest due message has been waiting ${formatNumber(
                  Math.floor(stats.data.oldest_due_seconds / 60),
                )} min`}
              >
                Workers may be overloaded or throttled by sender limits.{" "}
                <Link to="/workers">Check workers →</Link>
              </Alert>
            ) : null}

            <Card filledHeader>
              <Card.Header
                title="Key metrics"
                subtitle="current state"
                subtitlePlacement="right"
              />
              <Card.Content>
                {/* Six KPIs: a 3-col throughput row and a 3-col health row
                    (a forced 4-col grid leaves orphan cells). */}
                <div className={styles.counters}>
                  <div>
                    <SectionHeader title="Throughput" />
                    <KPIGrid columns={3}>
                  <KPI
                    label="Sent 24 h"
                    value={formatNumber(stats.data.sent_24h)}
                    delta="last 24 hours"
                  />
                  <KPI
                    label="Sent 7 d"
                    value={formatNumber(stats.data.sent_7d)}
                    delta="last 7 days"
                  />
                  <KPI
                    label="Queued"
                    value={formatNumber(stats.data.queued)}
                    delta="waiting to send"
                  />
                    </KPIGrid>
                  </div>
                  <div>
                    <SectionHeader title="Health" />
                    <KPIGrid columns={3}>
                  <KPI
                    label="Failed"
                    value={formatNumber(stats.data.failed)}
                    hi={stats.data.failed > 0}
                    delta={
                      stats.data.failed > 0 ? (
                        <Link to="/messages?state=failed">View failed →</Link>
                      ) : (
                        "none"
                      )
                    }
                  />
                  <KPI
                    label="Suppressions"
                    value={formatNumber(stats.data.suppressions)}
                    delta="all senders"
                  />
                  <KPI
                    label="Workers online"
                    value={formatNumber(stats.data.workers_online)}
                    hi={stats.data.workers_online === 0 && stats.data.queued > 0}
                    delta="connected now"
                  />
                    </KPIGrid>
                  </div>
                </div>
              </Card.Content>
            </Card>

            <Card>
              <Card.Header
                title="Volume by sender"
                subtitle="sent · last 7 days"
                subtitlePlacement="right"
              />
              <Card.Content>
                <RankedBars
                  caption="Messages sent by sender over the last 7 days"
                  labelHeader="Sender"
                  valueHeader="Messages sent"
                  data={stats.data.sender_volume_7d.map((s) => ({
                    label: s.sender,
                    value: s.count,
                  }))}
                />
              </Card.Content>
            </Card>

            <p className={styles.build}>Letterman {runtimeConfig.version}</p>
          </>
        ) : null}
      </Page.Content>
    </Page>
  );
}
